import { HttpClient, provideHttpClient, withInterceptors } from '@angular/common/http';
import { ApplicationConfig, inject, provideAppInitializer } from '@angular/core';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideRouter, withComponentInputBinding, withInMemoryScrolling, withViewTransitions } from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';
import { provideTranslateService, TranslateLoader } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { providePrimeNG } from 'primeng/config';

import { authInterceptorFn } from '@auth/interceptors/auth.interceptor';
import { AuthKind } from '@auth/models/auth';
import { AuthService } from '@auth/services/auth.service';
import { MsalAuthProvider } from '@auth/services/msal-auth-provider';
import { loaderInterceptorFn } from '@core/interceptors/loader.interceptor';
import { LogService } from '@core/services/log.service';
import { NotificationsService } from '@core/services/notifications.service';
import { environment } from '@environment/environment';

import { AppRoutes } from './app.routes';
import YtPreset from './primeng.preset';

export const appConfig: ApplicationConfig = {
  providers: [
    { provide: NotificationsService, useClass: NotificationsService },
    { provide: MessageService, useClass: MessageService },
    providePrimeNG({
      theme: {
        preset: YtPreset,
        options: {
          darkModeSelector: '.yt-app-dark'
        }
      }
    }),
    provideServiceWorker('ngsw-worker.js', {
      enabled: environment.production,
      // Register the ServiceWorker as soon as the app is stable
      // or after 30 seconds (whichever comes first).
      registrationStrategy: 'registerWhenStable:30000'
    }),
    provideHttpClient(withInterceptors([loaderInterceptorFn, authInterceptorFn])),
    // Read the MSAL sign-in redirect's return once at startup; a completed sign-in is applied to the session here so
    // the rest of the app sees it through AuthService. The composition root may know MSAL specifically — AuthService
    // itself stays provider-agnostic.
    provideAppInitializer(async () => {
      const msalProvider = inject(MsalAuthProvider);
      const authService = inject(AuthService);
      const logService = inject(LogService);
      try {
        const session = await msalProvider.handleRedirectResult();
        if (session) {
          authService.applySession(session, AuthKind.Msal);
        }
      } catch (error) {
        logService.error('[AppConfig] Failed to handle the MSAL sign-in redirect', { error });
      }
    }),
    provideTranslateService(),
    provideAnimationsAsync(),
    provideRouter(
      AppRoutes,
      withViewTransitions(),
      withInMemoryScrolling({ scrollPositionRestoration: 'enabled' }),
      withComponentInputBinding()
    )
  ]
};
